/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Stack, Box, Divider} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getBonusProductsForSpecificCartItem} from '@salesforce/retail-react-app/app/utils/bonus-product/cart'

/**
 * Fragment component that renders cart items with bonus products grouped with their qualifying products
 * @param {Object} props - Component props
 * @param {Array} props.nonBonusProducts - Array of non-bonus products
 * @param {Object} props.basket - The current basket data
 * @param {Object} props.productsWithPromotions - Products with promotion data
 * @param {Object} props.ruleBasedQualifyingProductsMap - Map of promotionId to Set of qualifying productIds for rule-based promotions
 * @param {boolean} props.isPromotionDataLoading - Whether promotion data is loading
 * @param {Function} props.renderProductItem - Function to render individual product items
 * @param {Function} props.getPromotionCalloutText - Function to get promotion text
 * @param {Function} props.onSelectBonusProducts - Callback when select bonus products button is clicked
 * @returns {JSX.Element} The grouped cart product list
 */
const CartProductListWithGroupedBonusProducts = ({
    nonBonusProducts,
    basket,
    productsWithPromotions,
    ruleBasedQualifyingProductsMap = {},
    isPromotionDataLoading,
    renderProductItem,
    hideBorder = false
}) => {
    // Fallback: if no non-bonus products, render all products in simple layout
    if (!nonBonusProducts || nonBonusProducts.length === 0) {
        return (
            <Stack gap={4}>
                {basket.productItems?.map((productItem, idx) =>
                    renderProductItem(productItem, idx)
                )}
            </Stack>
        )
    }

    return (
        <Stack gap={0} className="non-bonus-products-wrapper">
            {nonBonusProducts
                ?.filter(({c_hiddenFluidProduct}) => !c_hiddenFluidProduct)
                .map((qualifyingProduct, qualifyingIdx) => {
                    // Skip bonus product logic if promotion data is not loaded
                    if (!productsWithPromotions || isPromotionDataLoading) {
                        return (
                            <Box key={qualifyingProduct.itemId} className="render-product-wrapper">
                                {renderProductItem(qualifyingProduct, qualifyingIdx)}
                            </Box>
                        )
                    }

                    // Enhanced rendering for eligible products
                    try {
                        // Get bonus products allocated specifically to this cart item
                        const bonusProductsForThisProduct = getBonusProductsForSpecificCartItem(
                            basket,
                            qualifyingProduct,
                            productsWithPromotions,
                            ruleBasedQualifyingProductsMap
                        )

                        const hasBonusProductsInCart = bonusProductsForThisProduct.length > 0

                        return (
                            <Box
                                key={qualifyingProduct.itemId}
                                className="product-group"
                                data-testid={`product-group-${qualifyingProduct.productId}`}
                                layerStyle={hideBorder ? 'card' : 'cardBordered'}
                                backgroundColor="white"
                                {...(hideBorder
                                    ? {
                                          border: 'none',
                                          borderWidth: '0px',
                                          borderRadius: 'base'
                                      }
                                    : {
                                          borderWidth: '1px',
                                          borderColor: 'gray.200',
                                          borderRadius: 'base'
                                      })}
                            >
                                {/* Main product */}
                                <Box className="main-product">
                                    {renderProductItem(qualifyingProduct, qualifyingIdx, {
                                        hideBorder: true
                                    })}
                                </Box>

                                {/* Bonus products already in cart */}
                                {hasBonusProductsInCart && (
                                    <Box className="bonus-products-wrapper">
                                        <Stack gap={0} className="bonus-products-stack">
                                            {bonusProductsForThisProduct.map(
                                                (bonusProduct, bonusIdx) => {
                                                    const isLastBonusProduct =
                                                        bonusIdx ===
                                                        bonusProductsForThisProduct.length - 1

                                                    return (
                                                        <>
                                                            {bonusIdx == 0 && (
                                                                <Divider
                                                                    borderColor="gray.700"
                                                                    margin="30px 0px 30px 0px"
                                                                    className="divider-line"
                                                                    orientation="horizontal"
                                                                />
                                                            )}
                                                            <Box
                                                                key={bonusProduct.itemId}
                                                                className="bonus-product"
                                                                data-testid={`bonus-product-${bonusProduct.productId}`}
                                                                border="none"
                                                                borderBottom="none"
                                                                borderTop="none"
                                                                borderLeft="none"
                                                                borderRight="none"
                                                            >
                                                                {renderProductItem(
                                                                    bonusProduct,
                                                                    bonusIdx,
                                                                    {
                                                                        showQuantitySelector: false,
                                                                        hideBorder: true,
                                                                        hideBottomBorder:
                                                                            isLastBonusProduct
                                                                    }
                                                                )}
                                                            </Box>
                                                            {!isLastBonusProduct && (
                                                                <Divider
                                                                    borderColor="gray.700"
                                                                    margin="30px 0px 30px 0px"
                                                                    className="divider-line"
                                                                    orientation="horizontal"
                                                                />
                                                            )}
                                                        </>
                                                    )
                                                }
                                            )}
                                        </Stack>
                                    </Box>
                                )}
                                {(qualifyingIdx !== nonBonusProducts?.length - 1 ||
                                    ('giftCertificateItems' in basket &&
                                        basket.giftCertificateItems?.length)) && (
                                    <Divider
                                        borderColor="gray.700"
                                        margin="30px 0px 30px 0px"
                                        className="divider-line"
                                        orientation="horizontal"
                                    />
                                )}
                            </Box>
                        )
                    } catch (error) {
                        console.error('Error in enhanced rendering:', error)
                        // Fallback to simple rendering if enhanced fails
                        return (
                            <Box key={qualifyingProduct.itemId} className="simple-rendering-card">
                                {renderProductItem(qualifyingProduct, qualifyingIdx)}
                            </Box>
                        )
                    }
                })}
        </Stack>
    )
}

CartProductListWithGroupedBonusProducts.propTypes = {
    nonBonusProducts: PropTypes.arrayOf(
        PropTypes.shape({
            itemId: PropTypes.string,
            productId: PropTypes.string
        })
    ).isRequired,
    basket: PropTypes.shape({
        productItems: PropTypes.arrayOf(
            PropTypes.shape({
                itemId: PropTypes.string,
                productId: PropTypes.string,
                bonusProductLineItem: PropTypes.bool
            })
        ),
        giftCertificateItems: PropTypes.array
    }).isRequired,
    productsWithPromotions: PropTypes.object,
    ruleBasedQualifyingProductsMap: PropTypes.object,
    isPromotionDataLoading: PropTypes.bool.isRequired,
    renderProductItem: PropTypes.func.isRequired,
    getPromotionCalloutText: PropTypes.func.isRequired,
    onSelectBonusProducts: PropTypes.func.isRequired,
    hideBorder: PropTypes.bool
}

export default CartProductListWithGroupedBonusProducts
